import { Check, ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatLabel } from "@/components/reports/report-item/report-format";
import type { ReportFormat } from "@/types/api";

import { EXPORT_FORMATS } from "./export-options";

interface ExportFormatPickerProps {
  value: ReportFormat;
  onChange: (next: ReportFormat) => void;
  disabled?: boolean;
}

/**
 * Which file the Export button will produce.
 *
 * Sits beside Export rather than inside a dialog: someone sending a weekly
 * update wants one tap, and the format they used last is nearly always the one
 * they want again — so the common path costs nothing and only a deliberate
 * change costs a second tap. See [[collection-exports-section.spec]].
 */
export function ExportFormatPicker({
  value,
  onChange,
  disabled = false,
}: ExportFormatPickerProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          aria-label={`Export format: ${formatLabel(value)}`}
        >
          {formatLabel(value)}
          <ChevronDown className="ml-1 size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {EXPORT_FORMATS.map((format) => (
          // 44px, not the menu's stock ~32px. The cost of a mis-tap here is not
          // symmetric with a mis-tap anywhere else in the app: picking the
          // wrong format starts a multi-minute job that produces the wrong
          // file, and the only undo is waiting for it and exporting again.
          // Gloved hands in sunlight get a full-size target for that choice.
          <DropdownMenuItem
            key={format}
            onSelect={() => onChange(format)}
            className="min-h-11"
          >
            <span className="flex-1">{formatLabel(format)}</span>
            {value === format && <Check className="size-4" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
